// Compatibility for existing out-of-scope Overview/Reference/Cascade/Backtest imports.
// The retained Flow interface does not use these specimen declarations or calculations.
// Remove only when those surfaces enter their own authorised implementation pass.
import { C } from './tokens';

// ── CONSTANTS ────────────────────────────────────────────────────────────────
const Y = 0.7;                    // the impact-law coefficient
const DOMAIN_FRACTION = 0.10;     // one tier at the domain limit = 10% of daily volume
const VIX_BASELINE = 17.4;        // the design's baseline; σ is quoted against it

// ── TYPES ────────────────────────────────────────────────────────────────────

export type MackSession = 'asia' | 'europe' | 'us' | 'overlap';

export interface MackInstrumentCapacity {
  symbol: string;
  asset_class: 'FX' | 'Commodity' | 'Index';
  turnover: string;          // display string; context, not an input to the maths
  sigma_daily: number;       // percent, at VIX_BASELINE
  is_estimated: boolean;     // renders the EST tag
  session_movers: MackSession[];
  // NO ceiling_1 / multiple / band / R / character FIELDS. All five are derived
  // from (sigma_daily, vix, target) — see computeCapacity. Storing them would
  // let them drift from the controls that produce them.
}

export interface MackClassifierBand {
  label: string;             // the legacy count the band is named for
  notional: number;          // the band as HELD — millions of USD
  likely_participant: string;
}

export interface MackSeasonalityWindow {
  window: string;
  markets: string;
  v_effect: number;          // fraction, negative = thinner book
  // NO capital_effect FIELD — it is exactly 1/(1+v_effect) − 1 and the design's
  // five rows all reproduce from that. Derived, not stored.
}

export interface MackRotationRow {
  stage: string;
  most: string;
  second: string;
  least: string;
}

// ── THE BANDS ────────────────────────────────────────────────────────────────
// R never reaches zero. Band 3 is a character, not an absence.
const BANDS = [
  { n: 1, ceiling: 40,       label: 'Band 1 · impact',    R: 1,    accent: C.teal,
    character: 'Single tier or small coalition' },
  { n: 2, ceiling: 400,      label: 'Band 2 · coalition', R: 0.5,  accent: C.amberBright,
    character: 'Multi-tier coalition' },
  { n: 3, ceiling: Infinity, label: 'Band 3 · repricing', R: 0.35, accent: C.violet,
    // No Band 3 row appears in the design, so this string is written to the
    // register of the other two rather than transcribed. FLAGGED.
    character: 'Repricing — flow followed, not pushed' },
] as const;

// Sampled: the tier-equivalent figure and R are colour-coded by band (Band 1
// green, Band 2 amber); the BAND LABEL ITSELF is muted grey in the design, not
// colour-coded. The brief said band and R were both coded — the image disagrees
// and the image wins.

// ── COMPUTATION ──────────────────────────────────────────────────────────────

export interface CapacityResult {
  sigma_scaled: number;
  ceiling_1: number;
  multiple: number;          // raw
  multiple_shown: number;    // rounded — what the table prints
  band: typeof BANDS[number];
}

export function computeCapacity(sigmaDaily: number, vix: number, target: number): CapacityResult {
  // σ scales by the square root of the VIX ratio, per instrument.
  const sigma_scaled = sigmaDaily * Math.sqrt(vix / VIX_BASELINE);
  const ceiling_1 = Y * sigma_scaled * Math.sqrt(DOMAIN_FRACTION);
  // The SQUARE is the whole point — capital goes as target², not target.
  const multiple = ceiling_1 > 0 ? Math.pow(target / ceiling_1, 2) : 0;
  const multiple_shown = Math.round(multiple);
  // Band off the ROUNDED figure so the printed multiple and the printed band can
  // never contradict each other (a raw 40.4 printing as "40×" beside "Band 2"
  // would read as a bug). Both agree on every row of the design.
  const band = BANDS.find(b => multiple_shown <= b.ceiling)!;
  return { sigma_scaled, ceiling_1, multiple, multiple_shown, band };
}

// Capital effect is the reciprocal of the volume effect: a book 25% thinner
// makes the same move 1/0.75 = 33% cheaper. All five design rows reproduce.
export function capitalEffect(vEffect: number): number {
  return 1 / (1 + vEffect) - 1;
}

// ── PLACEHOLDER DATA ─────────────────────────────────────────────────────────
// Twelve instruments, TRANSCRIBED VERBATIM from the design — symbol, class,
// turnover and σ. Everything derived (ceiling₁, multiple, band, R, character)
// is computed and reproduces the design exactly at its slider positions.
//
// BASE ORDER matters: the design lists GBP/JPY, XAU/USD, DAX first, which are
// exactly the EUROPE-session movers, with the rest following in class order.
// The rule read from that: the active session's movers are promoted to the top,
// relative order otherwise preserved. Only one session is visible in the design,
// so the promotion rule is inferred from a single sample — FLAGGED, as are the
// mover sets for Asia / US / Overlap.
export const MACK_FLOW_INSTRUMENTS: MackInstrumentCapacity[] = [
  { symbol: 'GBP/JPY',    asset_class: 'FX',        turnover: '$230B',  sigma_daily: 0.70, is_estimated: false, session_movers: ['europe', 'asia'] },
  { symbol: 'USD/JPY',    asset_class: 'FX',        turnover: '$1.65T', sigma_daily: 0.55, is_estimated: false, session_movers: ['asia'] },
  { symbol: 'EUR/USD',    asset_class: 'FX',        turnover: '$2.03T', sigma_daily: 0.45, is_estimated: false, session_movers: ['overlap'] },
  { symbol: 'GBP/USD',    asset_class: 'FX',        turnover: '$980B',  sigma_daily: 0.55, is_estimated: false, session_movers: ['overlap'] },
  // Added build-10 (ruling 1). Turnover and sigma are REUSED FROM INPUTS, where
  // both instruments already carry them — nothing here is a new figure.
  { symbol: 'EUR/GBP',    asset_class: 'FX',        turnover: '$410B',  sigma_daily: 0.40, is_estimated: false, session_movers: ['europe'] },
  { symbol: 'AUD/JPY',    asset_class: 'FX',        turnover: '$205B',  sigma_daily: 0.70, is_estimated: false, session_movers: ['asia'] },
  { symbol: 'XAU/USD',    asset_class: 'Commodity', turnover: '$150B',  sigma_daily: 1.10, is_estimated: false, session_movers: ['europe'] },
  { symbol: 'DAX',        asset_class: 'Index',     turnover: '$22B',   sigma_daily: 1.10, is_estimated: false, session_movers: ['europe'] },
  { symbol: 'Nasdaq 100', asset_class: 'Index',     turnover: '$275B',  sigma_daily: 1.30, is_estimated: true,  session_movers: ['us'] },
  { symbol: 'S&P 500',    asset_class: 'Index',     turnover: '$350B',  sigma_daily: 0.90, is_estimated: true,  session_movers: ['us', 'overlap'] },
  { symbol: 'Dow',        asset_class: 'Index',     turnover: '$150B',  sigma_daily: 0.85, is_estimated: false, session_movers: ['us'] },
  { symbol: 'FTSE 100',   asset_class: 'Index',     turnover: '$8B',    sigma_daily: 0.75, is_estimated: false, session_movers: [] },
  { symbol: 'Brent',      asset_class: 'Commodity', turnover: '$45B',   sigma_daily: 1.80, is_estimated: true,  session_movers: [] },
  { symbol: 'WTI',        asset_class: 'Commodity', turnover: '$32B',   sigma_daily: 1.90, is_estimated: true,  session_movers: ['us'] },
];

// Notional is the band AS HELD; the contract count is back-solved at live price.
// The contract is 100 oz gold at the $3,350 the Inputs tab carries → $335,000 a
// contract. That link is INFERRED (the design shows no price) but reproduces all
// three notionals exactly. Note the 35+ band back-solving to 36 — that is not a
// rounding slip, it is the drift the panel exists to warn about.

export const MACK_CLASSIFIER_BANDS: MackClassifierBand[] = [
  { label: '16+', notional: 5.36,  likely_participant: 'Hedge funds · high-speed algos' },
  { label: '30+', notional: 10.05, likely_participant: 'Institutional scout orders' },
  { label: '35+', notional: 11.73, likely_participant: 'Institutional block execution' },
];

export const MACK_SEASONALITY: MackSeasonalityWindow[] = [
  { window: 'August',             markets: 'European FX, DAX, FTSE',  v_effect: -0.25 },
  { window: 'Golden Week',        markets: 'JPY crosses',             v_effect: -0.30 },
  { window: 'Chinese New Year',   markets: 'Gold, AUD, commodities',  v_effect: -0.20 },
  { window: 'Christmas–New Year', markets: 'All',                     v_effect: -0.40 },
  { window: 'Thanksgiving week',  markets: 'US indices, USD',         v_effect: -0.22 },
];

export const MACK_ROTATION: MackRotationRow[] = [
  { stage: 'Deflation',   most: 'FX · safe havens',    second: 'Commodities', least: 'Indices' },
  { stage: 'Stagflation', most: 'Commodities · gold',  second: 'FX',          least: 'Indices' },
  { stage: 'Expansion',   most: 'Indices',             second: 'FX',          least: 'Commodities' },
  { stage: 'Reflation',   most: 'Commodities',         second: 'Indices',     least: 'FX' },
];
